/**
 * Resolve an href to an in-app path for SPA navigation, or null if it should
 * leave the document (external, mailto, tel, new-tab modifiers, etc.).
 *
 * Uses `globalThis.location` (not `window`) so this module typechecks in Node
 * packages (e.g. `@vastra/api`) that do not include the DOM lib.
 */
const DEFAULT_STORE_ORIGINS = [
  'https://www.stylewithvikashini.com',
  'https://stylewithvikashini.com',
  'https://dev.stylewithvikashini.com',
];

function browserLocation(): { origin: string; href: string } | null {
  const loc = (globalThis as { location?: { origin?: unknown; href?: unknown } }).location;
  if (
    loc &&
    typeof loc.origin === 'string' &&
    loc.origin.length > 0 &&
    typeof loc.href === 'string' &&
    loc.href.length > 0
  ) {
    return { origin: loc.origin, href: loc.href };
  }
  return null;
}

function allowedOrigins(extra?: string[]): Set<string> {
  const set = new Set<string>();
  for (const o of [...DEFAULT_STORE_ORIGINS, ...(extra ?? [])]) {
    try {
      set.add(new URL(o).origin);
    } catch {
      // skip invalid
    }
  }
  const loc = browserLocation();
  if (loc) set.add(loc.origin);
  return set;
}

/** True when href is http(s) and not one of our storefront origins. */
export function isExternalHttpHref(href: string, extraOrigins?: string[]): boolean {
  const t = href.trim();
  if (!t || t.startsWith('#') || t.startsWith('mailto:') || t.startsWith('tel:') || t.startsWith('sms:')) {
    return false;
  }
  if (t.startsWith('/') && !t.startsWith('//')) return false;
  try {
    const loc = browserLocation();
    const base = loc?.href ?? 'https://www.stylewithvikashini.com/';
    const u = new URL(t, base);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    return !allowedOrigins(extraOrigins).has(u.origin);
  } catch {
    return true;
  }
}

/**
 * If this href should use React Router, return `pathname + search + hash`.
 * Otherwise null (browser default / new tab).
 */
export function resolveInAppNavigationPath(href: string, extraOrigins?: string[]): string | null {
  const t = href.trim();
  if (!t || t.startsWith('mailto:') || t.startsWith('tel:') || t.startsWith('sms:') || t.startsWith('javascript:')) {
    return null;
  }
  // Same-page hash only — let the browser scroll.
  if (t.startsWith('#')) return null;

  try {
    const loc = browserLocation();
    const base = loc?.href ?? 'https://www.stylewithvikashini.com/';
    const u = new URL(t, base);

    if (t.startsWith('/') && !t.startsWith('//')) {
      return `${u.pathname}${u.search}${u.hash}`;
    }

    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (!allowedOrigins(extraOrigins).has(u.origin)) return null;

    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }
}
